import type { ContactEventType } from '@/api/entity_types/ContactEvent';
import { ref, computed, type Ref } from 'vue';
import { useTeamStore } from '@/stores/team';
import { useAdminScheduleInput } from './useAdminScheduleInput';
import { useCandidateScheduleInput } from './useCandidateScheduleInput';

export type SchedulingMode = 'none' | 'admin_schedule' | 'candidate_choose';

export type UseScheduleInterviewInputArgs = {
  contact: Ref<{
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    canReceiveSms: boolean;
  }>;
  event_type: Ref<ContactEventType>;
};

export type UseScheduleInterviewInputReturn = ReturnType<typeof useScheduleInterviewInput>;

export const useScheduleInterviewInput = ({ contact, event_type }: UseScheduleInterviewInputArgs) => {
  const teamStore = useTeamStore();
  const schedulingMode = ref<SchedulingMode>('none');

  const adminSchedule = useAdminScheduleInput({ contact, event_type });
  const candidateSchedule = useCandidateScheduleInput({ contact, event_type });

  const isCalendly = computed(() => teamStore.team.calendar_option === 'Calendly');
  const isLoading = computed(
    () => !isCalendly.value && !adminSchedule.hasDefaults.value && teamStore.isLoadingCalendarConnections
  );

  const isReady = computed(() => {
    if (isCalendly.value) return !!teamStore.team.calendly_link;
    if (adminSchedule.hasDefaults.value) return true;
    return teamStore.membersWithCalendar.length > 0;
  });

  const settingsHint = computed(() => {
    if (isCalendly.value) return { text: 'add a Calendly link', href: '/settings/company/scheduling' };
    return { text: 'connect a calendar', href: '/settings/account/calendar' };
  });

  const adminButtonProps = computed(() => {
    if (isReady.value && isCalendly.value) {
      return { as: 'a', href: teamStore.team.calendly_link, target: '_blank' };
    }
    if (isReady.value) {
      return { onClick: () => (schedulingMode.value = 'admin_schedule') };
    }
    return {};
  });

  const validate = (): boolean => {
    if (schedulingMode.value === 'admin_schedule') return adminSchedule.validate();
    if (schedulingMode.value === 'candidate_choose') return candidateSchedule.validate();
    return true;
  };

  const submit = async () => {
    if (schedulingMode.value === 'admin_schedule') await adminSchedule.submit();
    if (schedulingMode.value === 'candidate_choose') await candidateSchedule.submit();
  };

  const isEditingCandidateOutreach = computed(() => candidateSchedule.view.value !== 'review');
  const showSchedulingModeBack = computed(() => schedulingMode.value !== 'none' && !isEditingCandidateOutreach.value);

  return {
    schedulingMode,
    adminSchedule,
    candidateSchedule,
    isLoading,
    isReady,
    settingsHint,
    adminButtonProps,
    isEditingCandidateOutreach,
    showSchedulingModeBack,
    validate,
    submit,
    navigateToCandidateReview: () => (candidateSchedule.view.value = 'review'),
  };
};
